import { Button, Tooltip, Typography } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import AddIcon from "@material-ui/icons/Add"
import { Flex } from "../../../lib"
// import { ZendeskIcon } from '../../lib/icons'
import { get } from "lodash"
import React, { useEffect, useState } from "react"
import { useNotify } from "react-admin"
import { generateCodeChallenge, generateCodeVerifier }
  from "../../../lib/utils/zendeskPkce"
import {
  getAccessToken,
  isAccessTokenExpired,
  refreshZendeskToken,
} from "../../../lib/utils/zendeskToken"
import getHttpsCallable from "../../../utils/getHttpsCallable"

const useStyles = makeStyles(theme => ({
  zendeskLinkButton: {
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}))


export async function api (path: string, options?: Record<string, unknown>) {
  if (!getAccessToken()) {
    startAuthFlow()
    return
  }

  // Proactively refresh when we know the access token has expired.
  if (isAccessTokenExpired()) {
    const refreshed = await refreshZendeskToken()
    if (!refreshed) {
      startAuthFlow()
      return
    }
  }

  function doFetch () {
    return fetch(
      `https://claimback.zendesk.com/api/v2/${path}`,
      {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
          ...(options?.headers as Record<string, string> | undefined),
        },
      },
    )
  }

  let result = await doFetch()

  // Reactively refresh once if the token was revoked/expired server-side.
  if (result.status === 401) {
    const refreshed = await refreshZendeskToken()
    if (!refreshed) {
      startAuthFlow()
      return
    }
    result = await doFetch()
  }

  return result.json()
}


const zendeskSearch = getHttpsCallable("admin-zendeskSearch")

// Finds Zendesk tickets linked to a claim through the server-side proxy
// (admin-zendeskSearch) instead of calling claimback.zendesk.com from the
// browser. Keeping the request on our own origin means it can't be blocked by
// an agent's ad blocker / privacy extension / corporate proxy — the cause of
// the "Failed to fetch" this panel used to throw on load. Token handling
// mirrors api(): bail into the sign-in flow when there is no usable token,
// refresh proactively when it is expired, and refresh-then-retry once when the
// server reports the token was rejected (401).
export async function searchClaimTickets (claimId: string | undefined) {
  if (!claimId) {
    return
  }

  if (!getAccessToken()) {
    startAuthFlow()
    return
  }

  if (isAccessTokenExpired()) {
    const refreshed = await refreshZendeskToken()
    if (!refreshed) {
      startAuthFlow()
      return
    }
  }

  async function callProxy () {
    const response = await zendeskSearch({
      accessToken: getAccessToken(),
      claimId,
    })
    return response.data
  }

  let result = await callProxy()

  // The proxy surfaces a rejected token as status 401 (rather than throwing) so
  // we can refresh and retry, exactly as the old direct-fetch path did.
  if (result?.status === 401) {
    const refreshed = await refreshZendeskToken()
    if (!refreshed) {
      startAuthFlow()
      return
    }
    result = await callProxy()
  }

  return result
}


async function createZendeskTicket (
  { requesterId, claimId }: { requesterId: string, claimId: string },
) {
  const result = await api("tickets.json", {
    method: "POST",
    body: JSON.stringify({
      ticket: {
        requester_id: requesterId,
        comment: { body: `Ihre Claimback Fall ID: ${claimId}`, public: false },
        custom_fields: [{ id: 360005022257, value: claimId }],
      },
    }),
  })
  return result
}


async function getRequester (claim: Claim) {
  const email = get(claim, "userEmail")
  const firstName = get(claim, "firstName", "") || ""
  const lastName = get(claim, "lastName", "") || ""
  let name = `${firstName} ${lastName}`

  if (!name.trim()) {
    name = email
  }

  if (!email) {
    throw new Error("Cannot create a zendesk user without an email")
  }

  const searchResults = await api(`users/search.json?query=email:${email}`)
  if (searchResults.count > 0) {
    // use this user
    const requester = get(searchResults, "users[0]")
    return requester
  }
  else {
    // create one and use that
    const result = await api("users.json", {
      method: "post",
      body: JSON.stringify({
        user: { name, email },
      }),
    })
    return result.user
  }
}


export async function startAuthFlow () {
  localStorage.setItem("zendesk_oauth_complete_url", window.location.href)

  const identifier = "claimback"
  const redirectUri = `${window.origin}/zendesk_oauth`
  const scope = "read write"

  const verifier = generateCodeVerifier()
  const challenge = await generateCodeChallenge(verifier)

  localStorage.setItem("zendesk_code_verifier", verifier)

  const url = new URL("https://claimback.zendesk.com/oauth/authorizations/new")
  url.searchParams.set("response_type", "code")
  url.searchParams.set("client_id", identifier)
  url.searchParams.set("redirect_uri", redirectUri)
  url.searchParams.set("scope", scope)
  url.searchParams.set("code_challenge", challenge)
  url.searchParams.set("code_challenge_method", "S256")

  // @ts-expect-error
  window.location = url.toString()
}


interface ZendeskPanelParams {
  claim: Claim;
  claimId: string;
  [x: string]: any;
}

export interface ZendeskTicket {
  id: string
  subject: string
  description: string
  created_at: string
}

export default function ZendeskPanel (
  { claim, claimId, ...props }: ZendeskPanelParams,
) {
  const classes = useStyles()
  const notify = useNotify()
  const [relatedTickets, setRelatedTickets] = useState<ZendeskTicket[]>([])

  const zendeskAuthed = getAccessToken()

  useEffect(() => {
    if (zendeskAuthed) {
      searchClaimTickets(claimId)
        .then(result => {
          // No result means the helper bailed into the sign-in flow.
          if (!result) return
          if (result.error) {
            return notify(`${result.error} ${result.description}`, "warning")
          }
          setRelatedTickets(result.results)
        })
        .catch(err => notify(err.message, "warning"))
    }
  }, [notify, claimId]) // eslint-disable-line

  return (
    <div {...props}>
      <Typography variant='subtitle2'>Zendesk Tickets</Typography>
      <Flex wrap>
        {relatedTickets && relatedTickets.map(zendeskTicket => (
          <Tooltip
            title={zendeskTicket.subject || zendeskTicket.description}
            key={zendeskTicket.id}
          >
            <Button
              href={
                `https://claimback.zendesk.com/agent/tickets/${
                  zendeskTicket.id}`
              }
              target='__blank'
              rel='noreferrer'
              size='small'
              className={classes.zendeskLinkButton}
              // startIcon={<ZendeskIcon />}
            >{zendeskTicket.id}
            </Button>
          </Tooltip>
        ))}
      </Flex>

      {zendeskAuthed
        ? (
          <Flex justify='end'>
            <Tooltip title='Create new Zendesk ticket'>
              <Button
                variant='outlined'
                size='small'
                startIcon={<AddIcon />}
                onClick={async () => {
                  const requester = await getRequester(claim)
                  const zendeskTicket = await createZendeskTicket(
                    {
                      claimId: get(claim, "id"),
                      requesterId: get(requester, "id"),
                    },
                  )
                  const win = window.open(
                    `https://claimback.zendesk.com/agent/tickets/${
                      get(zendeskTicket, "ticket.id")}`,
                    "_blank",
                  )
                  if (win) {
                    win.focus()
                  }
                }}
              >Add
              </Button>
            </Tooltip>
          </Flex>
        )
        : (
          <Flex justify='end'>
            <Button
              variant='outlined'
              size='small'
              // startIcon={<ZendeskIcon />}
              onClick={() => startAuthFlow()}
            >Sign in to Zendesk
            </Button>
          </Flex>
        )}
    </div>
  )
}
